import { useContext } from 'react'
import { useIntl } from 'react-intl'
import {
  Button,
  csx,
  type DataViewStatus,
  Flex,
  IconCaretDown,
  IconCaretRight,
  Skeleton,
} from '@vtex/admin-ui'
import { ShortcutBox } from '@/components/products-listing/components/ShortcutBox/ShortcutBox'
import {
  SkuListContext,
  useSKUListShortcut,
} from '@/components/products-listing/components/SKUList/contexts'
import { Tooltip } from '@/components/Tooltip'

import * as styles from './SKUListAction.styles'

interface SkuListActionProps {
  context: DataViewStatus
}

export function SkuListAction({ context }: SkuListActionProps) {
  const { isShortcutHintActive } = useSKUListShortcut()

  const { formatMessage } = useIntl()

  const { shouldShowAction, isExpanded, onToggle } = useContext(SkuListContext)

  if (context === 'loading') return <SkuListActionSkeleton />

  if (!shouldShowAction) return null

  return (
    <div className={csx(styles.container)}>
      <Tooltip
        enabled={isShortcutHintActive}
        text={
          <Flex direction="column">
            <span>
              {formatMessage(
                isExpanded
                  ? {
                      id: 'admin/admin.catalog.shortcuts.collapse-all',
                      defaultMessage: 'Collpase all',
                    }
                  : {
                      id: 'admin/admin.catalog.shortcuts.expand-all',
                      defaultMessage: 'Expand all',
                    }
              )}
            </span>
            <Flex
              direction="row"
              wrap="nowrap"
              justify="center"
              className={csx({ gap: '$space-1', marginTop: '$space-1' })}
            >
              <ShortcutBox>Shift</ShortcutBox>
              <ShortcutBox>
                {formatMessage({
                  id: 'admin/admin.catalog.shortcuts.click',
                  defaultMessage: 'Click',
                })}
              </ShortcutBox>
            </Flex>
          </Flex>
        }
      >
        <Button
          variant="neutralTertiary"
          onClick={onToggle}
          aria-label={formatMessage({
            id: isExpanded
              ? 'admin/admin.catalog.hide-skus-button-label'
              : 'admin/admin.catalog.view-skus-button-label',
            defaultMessage: isExpanded ? 'Hide SKUs' : 'View SKUs',
          })}
          icon={isExpanded ? <IconCaretDown /> : <IconCaretRight />}
        />
      </Tooltip>
    </div>
  )
}

function SkuListActionSkeleton() {
  return (
    <div className={csx(styles.container)}>
      <Skeleton className={csx({ height: '2rem', width: '2rem' })} />
    </div>
  )
}
